// Modified by AI on 06/09/2026. Edit #4.
import { chain, debounce, get } from 'lodash';

import React, {
    useState, useContext, useCallback, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants/queryKeys';
import TalentScoreHttpService from '../api/TalentScoreHttpService';
import Logger from '../services/logger';
import { HEALTH_METER_TYPES } from '../constants/HealthMeterTypes';
import SettingsContext from './SettingsContext';
import PositionContext from './PositionContext';
import {
    SearchCriteriaFields,
    SearchCriteriaFilterValues,
} from '../constants/SearchCriteria';

const TPA_DEBOUNCE_TIME_MS = 700;

export const getHealthMeterType = (fairConfig = {}, value = 0) => {
    const { min: minFair = 41, max: maxFair = 100 } = fairConfig;

    if (value < minFair) {
        return HEALTH_METER_TYPES.POOR;
    } if (value >= minFair && value <= maxFair) {
        return HEALTH_METER_TYPES.FAIR;
    }

    return HEALTH_METER_TYPES.OPTIMAL;
};

const TPAHealthMeterContext = React.createContext({});

export const TPAHealthMeterApp = (props) => {
    const { children } = props;
    const {
        systemConfiguration: {
            TPAHealthMeterRange = { min: 41, max: 100 },
            maxCurrentSearchInPercent = 100,
        },
    } = useContext(SettingsContext);
    const { positionId } = useContext(PositionContext);
    const queryClient = useQueryClient();
    const [currentFilter, setCurrentFilter] = useState(null);

    const { data: tpaData, isFetching, error } = useQuery({
        queryKey: QUERY_KEYS.tpaHealthMeter(positionId, currentFilter),
        queryFn: async () => {
            const tpaResponse = (await TalentScoreHttpService.getTotalTalentPoolAmount({ filter: currentFilter, positionId })) || 0;

            if (get(tpaResponse, 'isCancelled', false)) {
                return queryClient.getQueryData(QUERY_KEYS.tpaHealthMeter(positionId, currentFilter));
            }

            const tpaAmount = get(tpaResponse, 'totalPoolAmount', 0);

            const showOnlyDiversityTypes = chain(currentFilter)
                .get('selected')
                .find({ field: SearchCriteriaFields.diversity })
                .get('items', [])
                .filter({ required: SearchCriteriaFilterValues.filter })
                .map('uid')
                .value();
            const maxPercentage = maxCurrentSearchInPercent;

            const amount = Math.max(tpaAmount, 0);
            const stat = get(tpaResponse, 'stat', {});
            const diversity = Object.fromEntries(
                Object
                    .entries(stat.diversity || {})
                    .map(([key, value]) => [
                        key,
                        showOnlyDiversityTypes.includes(key) ? 100 : Math.min(maxPercentage, +value),
                    ]),
            );

            return {
                totalPoolAmount: amount,
                tpaStat: { ...stat, diversity },
                healthMeterType: getHealthMeterType(TPAHealthMeterRange, amount),
            };
        },
        enabled: currentFilter !== null && !!positionId,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });

    useEffect(() => {
        if (error) {
            Logger.error(error);
        }
    }, [error]);

    const totalPoolAmount = tpaData?.totalPoolAmount ?? 0;
    const tpaStat = tpaData?.tpaStat ?? {};
    const healthMeterType = tpaData?.healthMeterType ?? HEALTH_METER_TYPES.POOR;
    const isLoading = currentFilter === null || isFetching;

    const fetchTotalPoolAmount = useMemo(() => debounce((filter = {}) => {
        setCurrentFilter(filter);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.tpaHealthMeter(positionId, filter),
            exact: true,
        });
    }, TPA_DEBOUNCE_TIME_MS), [positionId, queryClient]);

    useEffect(() => () => fetchTotalPoolAmount.cancel(), [fetchTotalPoolAmount]);

    const resetTpaStat = useCallback(() => {
        setCurrentFilter(null);
        queryClient.removeQueries({ queryKey: QUERY_KEYS.tpaHealthMeterBase(positionId) });
    }, [positionId, queryClient]);

    const contextValue = useMemo(() => ({
        refreshTPA: fetchTotalPoolAmount,
        totalPoolAmount,
        tpaStat,
        isLoading,
        healthMeterType,
        resetTpaStat,
    }), [
        fetchTotalPoolAmount,
        totalPoolAmount,
        tpaStat,
        isLoading,
        healthMeterType,
        resetTpaStat,
    ]);

    return (
        <TPAHealthMeterContext.Provider
            value={contextValue}>
            {children}
        </TPAHealthMeterContext.Provider>
    );
};

TPAHealthMeterApp.propTypes = {
    children: PropTypes.node,
};

export default TPAHealthMeterContext;
